"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import type { ParcelPreview, ReportBundle, ReportJob } from "@/lib/parcelq/types";
import {
  Amenity_Rows_From_Api,
  Booklet_To_Agency_Rows,
  Booklet_To_Assessment_Rows,
  Booklet_To_Docs_Rows,
  Booklet_To_Hazard_Rows,
  Booklet_To_Incentive_Rows,
  Booklet_To_Parcel_Rows,
  Booklet_To_Public_Docs_Rows,
  Booklet_To_Red_Flags,
  Booklet_To_Setback_Rows,
  Booklet_To_Sources_Rows,
  Booklet_To_Tax_History_Rows,
  Booklet_To_Utility_Rows,
  Booklet_To_Zoning_Rows,
  ParcelPreview_To_Hazard_Rows,
  ParcelPreview_To_Zoning_Rows,
} from "@/lib/parcelq/mappers";
import { Api_Paths } from "@/lib/api/paths";
import { Auth_Event_Success, Auth_Prompt_Open } from "@/lib/auth/client/events";
import { Auth_Access_Get, Auth_Fetch, Auth_Session_Restore } from "@/lib/auth/client/session";
import {
  Error_Is_Abort,
  ParcelQ_Client,
  ParcelQ_Pending_Checkout_Key,
  ParcelQ_Pending_Search_Key,
  Tier_Id_To_Bundle,
  Bundle_To_Tier_Meta,
  type ParcelQ_Pending_Checkout,
} from "@/lib/parcelq/client";
import { Search_Query_Check_Run } from "@/lib/parcelq/search-query";
import {
  Duration_Avg_Resolve,
  Duration_Sample_Record,
  Job_Duration_Ms_From_Timestamps,
  Pipeline_Duration_Defaults_Ms,
  type Pipeline_Progress_Phase,
} from "@/lib/parcelq/pipeline-progress";
import { Confirmation_State } from "./confirmation-state";
import { Dashboard_State } from "./dashboard-state";
import { ParcelQ_Header } from "./parcelq-header";
import { Pipeline_State } from "./pipeline-state";
import { Pricing_State } from "./pricing-state";
import { Search_State } from "./search-state";
import type {
  Amenity_Row,
  App_View,
  Pricing_Tier,
  Red_Flag_Row,
  Source_Row,
  Table_Row,
  Tax_History_Row,
} from "./types";

async function Auth_Session_Ensure(): Promise<boolean> {
  try {
    const Token = Auth_Access_Get() ?? (await Auth_Session_Restore());
    if (!Token) {
      return false;
    }
    const Response = await Auth_Fetch(Api_Paths.V1.User_Me);
    return Response.ok;
  } catch {
    return false;
  }
}

type ParcelQ_App_Props = {
  Account_Show?: boolean;
  Header_Show?: boolean;
  Below_Search?: ReactNode;
};

export function ParcelQ_App({ Account_Show = true, Header_Show = true, Below_Search }: ParcelQ_App_Props) {
  const Router = useRouter();
  const Search_Params = useSearchParams();

  const [View_Current, View_Set] = useState<App_View>("search");
  const [Query_Text, Query_Set] = useState("");
  const [Parcel_Preview, Parcel_Preview_Set] = useState<ParcelPreview | null>(null);
  const [Tier_Selected, Tier_Set] = useState<Pricing_Tier | null>(null);
  const [Bundle_Selected, Bundle_Set] = useState<ReportBundle | null>(null);
  const [Job_Current, Job_Set] = useState<ReportJob | null>(null);
  const [Amenities_Rows, Amenities_Set] = useState<Amenity_Row[]>([]);
  const [Parcel_Rows, Parcel_Rows_Set] = useState<Table_Row[]>([]);
  const [Assessment_Rows, Assessment_Rows_Set] = useState<Table_Row[]>([]);
  const [Tax_History_Rows, Tax_History_Rows_Set] = useState<Tax_History_Row[]>([]);
  const [Zoning_Rows, Zoning_Set] = useState<Table_Row[]>([]);
  const [Setback_Rows, Setback_Set] = useState<Table_Row[]>([]);
  const [Hazard_Rows, Hazard_Set] = useState<Table_Row[]>([]);
  const [Incentive_Rows, Incentive_Rows_Set] = useState<Table_Row[]>([]);
  const [Utility_Rows, Utility_Rows_Set] = useState<Table_Row[]>([]);
  const [Agency_Rows, Agency_Rows_Set] = useState<Table_Row[]>([]);
  const [Public_Docs_Rows, Public_Docs_Rows_Set] = useState<Table_Row[]>([]);
  const [Docs_Rows, Docs_Rows_Set] = useState<Table_Row[]>([]);
  const [Sources_Rows, Sources_Rows_Set] = useState<Source_Row[]>([]);
  const [Red_Flags, Red_Flags_Set] = useState<Red_Flag_Row[]>([]);
  const [Time_Saved_Hours, Time_Saved_Hours_Set] = useState<number | undefined>(undefined);
  const [Time_Saved_Pipeline_Minutes, Time_Saved_Pipeline_Minutes_Set] = useState<number | undefined>(
    undefined,
  );
  const [Error_Message, Error_Set] = useState<string | null>(null);
  const [Correction_Message, Correction_Set] = useState<string | null>(null);
  const [Is_Loading, Loading_Set] = useState(false);
  const [Is_Cancelling, Cancelling_Set] = useState(false);
  const [Pipeline_Phase, Pipeline_Phase_Set] = useState<Pipeline_Progress_Phase>("queued");
  const [Avg_Duration_Ms, Avg_Duration_Ms_Set] = useState(Pipeline_Duration_Defaults_Ms.complete);
  const Search_Abort_Ref = useRef<AbortController | null>(null);
  const Pipeline_Abort_Ref = useRef<AbortController | null>(null);
  const Search_Submit_Ref = useRef<(Query_Override?: string) => Promise<void>>(async () => {});

  const Pipeline_Start = useCallback(
    async (
      Bundle: ReportBundle,
      Tier: Pricing_Tier,
      Payment_Session_Id: string | null,
      Radius_Miles?: number,
    ) => {
      if (!Parcel_Preview?.apn) {
        Error_Set("Missing parcel APN. Search again.");
        View_Set("search");
        return;
      }

      Pipeline_Abort_Ref.current?.abort();
      const Abort_Controller = new AbortController();
      Pipeline_Abort_Ref.current = Abort_Controller;

      Error_Set(null);
      Cancelling_Set(false);
      Tier_Set(Tier);
      Bundle_Set(Bundle);
      View_Set("generating");
      Pipeline_Phase_Set("queued");

      let Stats = null;
      try {
        Stats = await ParcelQ_Client.reports.Stats_Load();
      } catch {
        Stats = null;
      }
      Avg_Duration_Ms_Set(Duration_Avg_Resolve(Bundle, Stats));

      try {
        const Job = await ParcelQ_Client.reports.Job_Generate(
          {
            apn: Parcel_Preview.apn,
            address: Parcel_Preview.address || undefined,
            city: Parcel_Preview.city || undefined,
            bundle: Bundle,
            radius_miles: Radius_Miles,
            payment_session_id: Payment_Session_Id ?? undefined,
          },
          Abort_Controller.signal,
        );
        if (Abort_Controller.signal.aborted) {
          void ParcelQ_Client.reports.Job_Delete(Job.id).catch(() => undefined);
          return;
        }
        Job_Set(Job);
        Pipeline_Phase_Set("running");

        const Job_Final = await ParcelQ_Client.reports.Job_Poll(
          Job.id,
          (Job_Update) => {
            Job_Set(Job_Update);
            Pipeline_Phase_Set(Job_Update.status === "queued" ? "queued" : "running");
          },
          2500,
          Abort_Controller.signal,
        );

        if (Job_Final.status === "failed") {
          throw new Error(Job_Final.error || "Pipeline failed");
        }

        if (Job_Final.finished_at) {
          Duration_Sample_Record(
            Bundle,
            Job_Duration_Ms_From_Timestamps(
              Job_Final.running_at || Job_Final.started_at,
              Job_Final.finished_at,
            ),
          );
        }

        Pipeline_Phase_Set("loading");

        const [Booklet, Amenities] = await Promise.all([
          ParcelQ_Client.reports.Booklet_Load(Job_Final.id),
          ParcelQ_Client.amenities.List(Job_Final.apn || Parcel_Preview.apn),
        ]);

        if (Abort_Controller.signal.aborted) {
          return;
        }

        Parcel_Rows_Set(Booklet_To_Parcel_Rows(Booklet));
        Assessment_Rows_Set(Booklet_To_Assessment_Rows(Booklet));
        Tax_History_Rows_Set(Booklet_To_Tax_History_Rows(Booklet));
        Zoning_Set(Booklet_To_Zoning_Rows(Booklet));
        Setback_Set(Booklet_To_Setback_Rows(Booklet));
        Hazard_Set(Booklet_To_Hazard_Rows(Booklet));
        Incentive_Rows_Set(Booklet_To_Incentive_Rows(Booklet));
        Utility_Rows_Set(Booklet_To_Utility_Rows(Booklet));
        Agency_Rows_Set(Booklet_To_Agency_Rows(Booklet));
        Public_Docs_Rows_Set(Booklet_To_Public_Docs_Rows(Booklet));
        Docs_Rows_Set(Booklet_To_Docs_Rows(Booklet));
        Sources_Rows_Set(Booklet_To_Sources_Rows(Booklet));
        Red_Flags_Set(Booklet_To_Red_Flags(Booklet));
        Amenities_Set(Amenity_Rows_From_Api(Amenities.amenities));
        Time_Saved_Hours_Set(Tier.Hours_Saved ?? Bundle_To_Tier_Meta(Bundle).Hours_Saved);
        if (Job_Final.finished_at) {
          const Ms = Job_Duration_Ms_From_Timestamps(
            Job_Final.running_at || Job_Final.started_at,
            Job_Final.finished_at,
          );
          Time_Saved_Pipeline_Minutes_Set(Math.max(1, Math.round(Ms / 60000)));
        }
        Job_Set(Job_Final);
        View_Set("dashboard");
      } catch (Caught) {
        if (Error_Is_Abort(Caught) || Abort_Controller.signal.aborted) {
          return;
        }
        Error_Set(Caught instanceof Error ? Caught.message : "Pipeline failed");
        View_Set("pricing");
      } finally {
        if (Pipeline_Abort_Ref.current === Abort_Controller) {
          Pipeline_Abort_Ref.current = null;
          Cancelling_Set(false);
        }
      }
    },
    [Parcel_Preview],
  );

  useEffect(() => {
    const Payment_Status = Search_Params.get("payment");
    const Session_Id = Search_Params.get("session_id");
    if (Payment_Status !== "success" || !Session_Id?.trim()) {
      return;
    }
    const Session_Id_Confirmed = Session_Id.trim();

    let Cancelled = false;

    async function Payment_Resume() {
      Loading_Set(true);
      Error_Set(null);

      try {
        const Pending_Raw = sessionStorage.getItem(ParcelQ_Pending_Checkout_Key);
        if (!Pending_Raw) {
          throw new Error("Checkout session expired. Select a report bundle again.");
        }

        const Pending = JSON.parse(Pending_Raw) as ParcelQ_Pending_Checkout;
        await ParcelQ_Client.payments.Checkout_Confirm(Session_Id_Confirmed);

        const Tier: Pricing_Tier = {
          Id: Pending.tier_id,
          Title: Pending.tier_title,
          Reports: "",
          Price: 0,
        };

        if (!Parcel_Preview && Pending.apn) {
          Parcel_Preview_Set(await ParcelQ_Client.parcels.By_Apn(Pending.apn));
        }

        sessionStorage.removeItem(ParcelQ_Pending_Checkout_Key);
        Router.replace("/parcelq");

        if (!Cancelled) {
          await Pipeline_Start(
            Tier_Id_To_Bundle(Pending.tier_id),
            Tier,
            Session_Id_Confirmed,
            Pending.radius_miles,
          );
        }
      } catch (Caught) {
        if (!Cancelled) {
          Error_Set(Caught instanceof Error ? Caught.message : "Payment confirmation failed");
          View_Set("pricing");
        }
      } finally {
        if (!Cancelled) {
          Loading_Set(false);
        }
      }
    }

    void Payment_Resume();

    return () => {
      Cancelled = true;
    };
  }, [Search_Params, Parcel_Preview, Pipeline_Start, Router]);

  useEffect(() => {
    const Job_Id_Raw = Search_Params.get("job")?.trim();
    if (!Job_Id_Raw) {
      return;
    }
    const Job_Id = Job_Id_Raw;

    Pipeline_Abort_Ref.current?.abort();
    const Abort_Controller = new AbortController();
    Pipeline_Abort_Ref.current = Abort_Controller;

    async function Job_Restore() {
      Loading_Set(true);
      Error_Set(null);
      Cancelling_Set(false);

      try {
        let Stats = null;
        try {
          Stats = await ParcelQ_Client.reports.Stats_Load();
        } catch {
          Stats = null;
        }

        let Job = await ParcelQ_Client.reports.Job_Get(Job_Id);
        if (Abort_Controller.signal.aborted) {
          return;
        }

        if (Job.status !== "completed" && Job.status !== "failed") {
          Loading_Set(false);
          View_Set("generating");
          Pipeline_Phase_Set(Job.status === "queued" ? "queued" : "running");
          Avg_Duration_Ms_Set(Duration_Avg_Resolve(Job.bundle, Stats));
          Job_Set(Job);

          Job = await ParcelQ_Client.reports.Job_Poll(
            Job_Id,
            (Job_Update) => {
              if (Abort_Controller.signal.aborted) {
                return;
              }
              Job_Set(Job_Update);
              Pipeline_Phase_Set(Job_Update.status === "queued" ? "queued" : "running");
            },
            2500,
            Abort_Controller.signal,
          );
        }

        if (Abort_Controller.signal.aborted) {
          return;
        }
        if (Job.status === "failed") {
          throw new Error(Job.error || "Pipeline failed");
        }

        const Apn = Job.apn || "";
        if (!Apn) {
          throw new Error("Job has no APN");
        }

        const [Booklet, Amenities, Preview] = await Promise.all([
          ParcelQ_Client.reports.Booklet_Load(Job.id),
          ParcelQ_Client.amenities.List(Apn),
          ParcelQ_Client.parcels.By_Apn(Apn).catch(() => null),
        ]);

        if (Abort_Controller.signal.aborted) {
          return;
        }

        const Tier_Meta = Bundle_To_Tier_Meta(Job.bundle);
        Tier_Set({
          Id: Tier_Meta.Id,
          Title: Tier_Meta.Title,
          Reports: Tier_Meta.Reports,
          Price: Tier_Meta.Price,
          Credits: Tier_Meta.Credits,
          Hours_Saved: Tier_Meta.Hours_Saved,
        });
        Bundle_Set(Job.bundle);
        Parcel_Preview_Set(
          Preview ?? {
            object_id: 0,
            apn: Apn,
            county: "",
            address: Job.address || Apn,
            city: "",
            state: "CA",
            zip_code: "",
            latitude: 0,
            longitude: 0,
            rings: [],
            supported: true,
            preview_only: true,
          },
        );
        Parcel_Rows_Set(Booklet_To_Parcel_Rows(Booklet));
        Assessment_Rows_Set(Booklet_To_Assessment_Rows(Booklet));
        Tax_History_Rows_Set(Booklet_To_Tax_History_Rows(Booklet));
        Zoning_Set(Booklet_To_Zoning_Rows(Booklet));
        Setback_Set(Booklet_To_Setback_Rows(Booklet));
        Hazard_Set(Booklet_To_Hazard_Rows(Booklet));
        Incentive_Rows_Set(Booklet_To_Incentive_Rows(Booklet));
        Utility_Rows_Set(Booklet_To_Utility_Rows(Booklet));
        Agency_Rows_Set(Booklet_To_Agency_Rows(Booklet));
        Public_Docs_Rows_Set(Booklet_To_Public_Docs_Rows(Booklet));
        Docs_Rows_Set(Booklet_To_Docs_Rows(Booklet));
        Sources_Rows_Set(Booklet_To_Sources_Rows(Booklet));
        Red_Flags_Set(Booklet_To_Red_Flags(Booklet));
        Amenities_Set(Amenity_Rows_From_Api(Amenities.amenities));
        Time_Saved_Hours_Set(Tier_Meta.Hours_Saved);
        if (Job.finished_at) {
          const Ms = Job_Duration_Ms_From_Timestamps(
            Job.running_at || Job.started_at,
            Job.finished_at,
          );
          Time_Saved_Pipeline_Minutes_Set(Math.max(1, Math.round(Ms / 60000)));
        }
        Job_Set(Job);
        View_Set("dashboard");
      } catch (Caught) {
        if (Error_Is_Abort(Caught) || Abort_Controller.signal.aborted) {
          return;
        }
        Error_Set(Caught instanceof Error ? Caught.message : "Could not open report job");
        View_Set("search");
      } finally {
        if (Pipeline_Abort_Ref.current === Abort_Controller) {
          Pipeline_Abort_Ref.current = null;
          Cancelling_Set(false);
        }
        if (!Abort_Controller.signal.aborted) {
          Loading_Set(false);
        }
      }
    }

    void Job_Restore();

    return () => {
      Abort_Controller.abort();
    };
  }, [Search_Params]);

  async function Search_HandleSubmit(Query_Override?: string) {
    const Query_Raw = (Query_Override ?? Query_Text).trim();
    if (!Query_Raw) {
      Correction_Set(null);
      Error_Set("Enter a California APN or street address.");
      return;
    }

    const Checked = Search_Query_Check_Run(Query_Raw);
    Query_Set(Checked.display || Query_Raw);
    Correction_Set(Checked.correction || null);

    if (!Checked.ok || !Checked.kind) {
      Error_Set(Checked.error || "Enter a California APN or street address.");
      return;
    }

    const Query_For_Lookup = Checked.query;
    const Query_For_Pending = Checked.display || Query_For_Lookup;

    const Authed = await Auth_Session_Ensure();
    if (!Authed) {
      sessionStorage.setItem(ParcelQ_Pending_Search_Key, Query_For_Pending);
      Auth_Prompt_Open({
        Mode: "login",
        Next_Path: typeof window !== "undefined" ? window.location.pathname || "/" : "/",
      });
      return;
    }

    Search_Abort_Ref.current?.abort();
    const Abort_Controller = new AbortController();
    Search_Abort_Ref.current = Abort_Controller;

    Loading_Set(true);
    Error_Set(null);

    try {
      const Preview =
        Checked.kind === "apn"
          ? await ParcelQ_Client.parcels.By_Apn(Query_For_Lookup, Abort_Controller.signal)
          : await ParcelQ_Client.parcels.By_Address(
              Query_For_Lookup,
              undefined,
              Abort_Controller.signal,
            );

      if (Abort_Controller.signal.aborted) {
        return;
      }

      Parcel_Preview_Set(Preview);
      Parcel_Rows_Set([]);
      Assessment_Rows_Set([]);
      Tax_History_Rows_Set([]);
      Incentive_Rows_Set([]);
      Utility_Rows_Set([]);
      Agency_Rows_Set([]);
      Public_Docs_Rows_Set([]);
      Docs_Rows_Set([]);
      Sources_Rows_Set([]);
      Zoning_Set(ParcelPreview_To_Zoning_Rows(Preview));
      Hazard_Set(ParcelPreview_To_Hazard_Rows(Preview));
      Setback_Set([]);
      View_Set("confirmation");
    } catch (Caught) {
      if (Error_Is_Abort(Caught) || Abort_Controller.signal.aborted) {
        return;
      }
      Error_Set(Caught instanceof Error ? Caught.message : "Parcel lookup failed");
    } finally {
      if (Search_Abort_Ref.current === Abort_Controller) {
        Search_Abort_Ref.current = null;
      }
      if (!Abort_Controller.signal.aborted) {
        Loading_Set(false);
      }
    }
  }

  Search_Submit_Ref.current = Search_HandleSubmit;

  useEffect(() => {
    async function Search_Pending_Resume() {
      const Pending_Raw = sessionStorage.getItem(ParcelQ_Pending_Search_Key)?.trim();
      if (!Pending_Raw) {
        return;
      }

      sessionStorage.removeItem(ParcelQ_Pending_Search_Key);

      const Authed = await Auth_Session_Ensure();
      if (!Authed) {
        sessionStorage.setItem(ParcelQ_Pending_Search_Key, Pending_Raw);
        return;
      }

      await Search_Submit_Ref.current(Pending_Raw);
    }

    void Search_Pending_Resume();

    function Auth_Success_Handle() {
      void Search_Pending_Resume();
    }

    window.addEventListener(Auth_Event_Success, Auth_Success_Handle);
    return () => window.removeEventListener(Auth_Event_Success, Auth_Success_Handle);
  }, []);

  function Search_HandleCancel() {
    Search_Abort_Ref.current?.abort();
    Search_Abort_Ref.current = null;
    Loading_Set(false);
    Error_Set(null);
  }

  async function Pipeline_HandleCancel() {
    const Job_Id = Job_Current?.id;
    Cancelling_Set(true);
    Pipeline_Abort_Ref.current?.abort();
    Pipeline_Abort_Ref.current = null;

    if (Job_Id) {
      try {
        await ParcelQ_Client.reports.Job_Delete(Job_Id);
      } catch {
        // Best-effort delete; UI still leaves generating view.
      }
    }

    Job_Set(null);
    Pipeline_Phase_Set("queued");
    Cancelling_Set(false);
    Error_Set(null);
    Loading_Set(false);
    View_Set(Parcel_Preview ? "pricing" : "search");
    if (Search_Params.get("job")) {
      Router.replace("/parcelq");
    }
  }

  async function Pricing_HandleSelect(
    Tier: Pricing_Tier,
    Radius_Miles: number,
    Credits_To_Redeem = 0,
  ) {
    if (!Parcel_Preview?.apn) {
      Error_Set("Missing parcel APN. Search again.");
      View_Set("search");
      return;
    }

    const Bundle = Tier_Id_To_Bundle(Tier.Id);
    Loading_Set(true);
    Error_Set(null);

    try {
      const Payment_Config = await ParcelQ_Client.payments.Config_Get();
      if (!Payment_Config.stripe_enabled) {
        await Pipeline_Start(Bundle, Tier, null, Radius_Miles);
        return;
      }

      const Checkout = await ParcelQ_Client.payments.Checkout_Create({
        apn: Parcel_Preview.apn,
        bundle: Bundle,
        radius_miles: Radius_Miles,
        credits_to_redeem: Credits_To_Redeem > 0 ? Credits_To_Redeem : undefined,
      });

      const Pending: ParcelQ_Pending_Checkout = {
        apn: Parcel_Preview.apn,
        bundle: Bundle,
        tier_id: Tier.Id,
        tier_title: Tier.Title,
        radius_miles: Radius_Miles,
      };
      sessionStorage.setItem(ParcelQ_Pending_Checkout_Key, JSON.stringify(Pending));
      window.location.href = Checkout.checkout_url;
    } catch (Caught) {
      const Message = Caught instanceof Error ? Caught.message : "Checkout failed";
      if (/stripe is not configured/i.test(Message)) {
        await Pipeline_Start(Bundle, Tier, null, Radius_Miles);
      } else {
        Error_Set(Message);
      }
    } finally {
      Loading_Set(false);
    }
  }

  function Dashboard_HandleRestart() {
    Query_Set("");
    Parcel_Preview_Set(null);
    Tier_Set(null);
    Bundle_Set(null);
    Job_Set(null);
    Pipeline_Phase_Set("queued");
    Amenities_Set([]);
    Parcel_Rows_Set([]);
    Assessment_Rows_Set([]);
    Tax_History_Rows_Set([]);
    Zoning_Set([]);
    Setback_Set([]);
    Hazard_Set([]);
    Incentive_Rows_Set([]);
    Utility_Rows_Set([]);
    Agency_Rows_Set([]);
    Public_Docs_Rows_Set([]);
    Docs_Rows_Set([]);
    Sources_Rows_Set([]);
    Red_Flags_Set([]);
    Time_Saved_Hours_Set(undefined);
    Time_Saved_Pipeline_Minutes_Set(undefined);
    Error_Set(null);
    View_Set("search");
  }

  return (
    <div className="flex flex-1 flex-col text-slate-100">
      {Header_Show ? <ParcelQ_Header View_Current={View_Current} Account_Show={Account_Show} /> : null}

      {Error_Message && View_Current !== "search" ? (
        <div className="mx-auto w-full max-w-6xl px-6 pt-4">
          <p className="rounded-lg border border-red-400/30 bg-red-950/50 px-4 py-3 text-sm text-red-300" role="alert">
            {Error_Message}
          </p>
        </div>
      ) : null}

      <main className="flex flex-1 flex-col">
        {View_Current === "search" && (
          <Search_State
            Query_Text={Query_Text}
            Query_OnChange={(Value) => {
              Query_Set(Value);
              if (Error_Message) {
                Error_Set(null);
              }
              if (Correction_Message) {
                Correction_Set(null);
              }
            }}
            Search_OnSubmit={Search_HandleSubmit}
            Search_OnCancel={Search_HandleCancel}
            Is_Loading={Is_Loading}
            Error_Message={Error_Message}
            Correction_Message={Correction_Message}
          />
        )}

        {View_Current === "search" && Below_Search ? Below_Search : null}

        {View_Current === "confirmation" && Parcel_Preview ? (
          <Confirmation_State
            Parcel={Parcel_Preview}
            Confirmation_OnBack={() => View_Set("search")}
            Confirmation_OnContinue={() => View_Set("pricing")}
          />
        ) : null}

        {View_Current === "pricing" && (
          <Pricing_State
            Pricing_OnBack={() => View_Set("confirmation")}
            Pricing_OnSelect={Pricing_HandleSelect}
            Is_Loading={Is_Loading}
            County={Parcel_Preview?.county}
          />
        )}

        {View_Current === "generating" && (
          <Pipeline_State
            Job={Job_Current}
            Avg_Duration_Ms={Avg_Duration_Ms}
            Phase={Pipeline_Phase}
            Pipeline_OnCancel={Pipeline_HandleCancel}
            Is_Cancelling={Is_Cancelling}
          />
        )}

        {View_Current === "dashboard" && Tier_Selected && (
          <Dashboard_State
            Tier_Title={Tier_Selected.Title}
            Apn={Parcel_Preview?.apn || Job_Current?.apn || ""}
            Job={Job_Current}
            Parcel_Rows={Parcel_Rows}
            Assessment_Rows={Assessment_Rows}
            Tax_History_Rows={Tax_History_Rows}
            Zoning_Rows={Zoning_Rows}
            Setback_Rows={Setback_Rows}
            Hazard_Rows={Hazard_Rows}
            Incentive_Rows={Incentive_Rows}
            Utility_Rows={Utility_Rows}
            Agency_Rows={Agency_Rows}
            Public_Docs_Rows={Public_Docs_Rows}
            Docs_Rows={Docs_Rows}
            Sources_Rows={Sources_Rows}
            Amenities_Rows={Amenities_Rows}
            Red_Flags={Red_Flags}
            Parcel_Latitude={Parcel_Preview?.latitude}
            Parcel_Longitude={Parcel_Preview?.longitude}
            Parcel_Rings={Parcel_Preview?.rings}
            Radius_Miles={Job_Current?.radius_miles}
            Time_Saved_Hours={Time_Saved_Hours}
            Time_Saved_Pipeline_Minutes={Time_Saved_Pipeline_Minutes}
            Dashboard_OnRestart={Dashboard_HandleRestart}
          />
        )}
      </main>
    </div>
  );
}
